<template>
  <div class="keyboard-tool-page">
    <div class="tool-header">
      <h1>输入法键盘背景预览工具</h1>
      <p>✨ 直接拖拽图片到按键上设置背景，或先选择图片再点击按键。支持QWERTY和九宫格两种布局。双击已设置背景的按键可移除图片</p>
    </div>
    
    <div class="tool-content">
      <!-- 工具栏 -->
      <div class="tool-controls">
        <div class="control-group">
          <label>皮肤编号：</label>
          <input 
            type="number" 
            v-model.number="keyboardTool.skinNumber"
            min="1"
            max="999"
            class="skin-number-input"
            placeholder="皮肤编号"
          />
        </div>
        
        <div class="control-group">
          <label>
            <input 
              type="checkbox" 
              v-model="keyboardTool.isPetSkin"
              class="pet-skin-checkbox"
            />
            桌宠皮肤
          </label>
        </div>
        
        <div class="control-group">
          <label>键盘类型：</label>
          <div class="keyboard-type-switch">
            <button 
              @click="keyboardTool.keyboardType = 'qwerty'"
              class="type-btn"
              :class="{ active: keyboardTool.keyboardType === 'qwerty' }"
            >
              QWERTY
            </button>
            <button 
              @click="keyboardTool.keyboardType = 'jiugongge'"
              class="type-btn"
              :class="{ active: keyboardTool.keyboardType === 'jiugongge' }"
            >
              九宫格
            </button>
          </div>
        </div>
        
        <!-- 添加大小写切换按钮，仅在QWERTY模式下显示 -->
        <div class="control-group" v-if="keyboardTool.keyboardType === 'qwerty'">
          <label>字母模式：</label>
          <div class="case-switch">
            <button 
              @click="keyboardTool.isUpperCase = true"
              class="case-btn"
              :class="{ active: keyboardTool.isUpperCase }"
            >
              大写 (A)
            </button>
            <button 
              @click="keyboardTool.isUpperCase = false"
              class="case-btn"
              :class="{ active: !keyboardTool.isUpperCase }"
            >
              小写 (a)
            </button>
          </div>
        </div>
        
        <div class="control-group">
          <label>按键图片：</label>
          <input 
            type="file" 
            ref="keyBgInput"
            @change="handleKeyBgSelect"
            accept="image/*"
            style="display: none;"
          />
          <button @click="$refs.keyBgInput.click()" class="control-btn">
            选择按键图片
          </button>
          <button @click="clearAllKeyBackgrounds" class="control-btn secondary">
            清除所有图片
          </button>
        </div>
        
        <div class="control-group">
          <label>
            <input 
              type="checkbox" 
              v-model="keyboardTool.showLabels"
              class="show-labels-checkbox"
            />
            显示按键文字
          </label>
        </div>
        
        <div class="control-group">
          <button @click="exportRenamedImages" class="control-btn success">
            导出重命名图片
          </button>
        </div>
      </div>
      
      <!-- 键盘预览区域 -->
      <div class="keyboard-preview-container">
        <div class="keyboard-container">
          <div class="drag-overlay" v-if="!hasAnyKeyBackground">
            <div class="drag-hint">
              <div class="drag-icon">🖼️</div>
              <p>直接拖拽图片到任意按键上</p>
              <p class="sub-hint">支持 JPG、PNG、GIF、WebP 格式</p>
            </div>
          </div>
          
          <!-- 键盘布局 -->
          <div 
            class="keyboard-layout"
            :class="{ 'jiugongge': keyboardTool.keyboardType === 'jiugongge' }"
          >
            <div 
              v-for="(row, rowIndex) in currentKeyboardLayout" 
              :key="rowIndex" 
              class="keyboard-row"
            >
                              <div
                  v-for="key in row"
                  :key="key.key"
                  class="keyboard-key"
                  :class="{ 
                    'has-background': keyboardTool.keyBackgrounds[key.key],
                    'jiugongge-key': keyboardTool.keyboardType === 'jiugongge',
                    'drag-over': keyboardTool.dragOverKey === key.key,
                    'show-labels': keyboardTool.showLabels && keyboardTool.keyBackgrounds[key.key]
                  }"
                  :style="getKeyStyle(key)"
                  @click="handleKeyClick(key)"
                  @dblclick="handleKeyDoubleClick(key)"
                  @drop="handleKeyDrop($event, key)"
                  @dragover="handleKeyDragOver($event)"
                  @dragenter="handleKeyDragEnter($event, key)"
                  @dragleave="handleKeyDragLeave($event)"
                >
                <div 
                  v-if="keyboardTool.keyBackgrounds[key.key]"
                  class="key-background"
                  :style="{
                    backgroundImage: `url(${keyboardTool.keyBackgrounds[key.key]})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }"
                ></div>
                                  <div class="key-content">
                    <span class="key-label">{{ key.label }}</span>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      

    </div>
  </div>
</template>

<script>
import JSZip from 'jszip'

export default {
  name: 'KeyboardTool',
  metaInfo: {
    title: '键盘背景工具 - 幸福输入'
  },
  data() {
    return {
      // 键盘工具相关数据
      keyboardTool: {
        keyboardType: 'qwerty', // 'qwerty' 或 'jiugongge'
        keyBackgrounds: {}, // 存储每个按键的背景图片
        draggedImage: null,
        dragOverKey: null, // 当前拖拽悬停的按键
        showLabels: false, // 是否显示有背景图片的按键文字
        skinNumber: 1, // 皮肤编号
        isUpperCase: true, // 字母模式，true为大写，false为小写
        isPetSkin: false // 是否为桌宠皮肤
      },
      // QWERTY键盘布局
      qwertyLayout: [
        [
          { key: '1', label: '1', x: 0, y: 0, width: 1, height: 1 },
          { key: '2', label: '2', x: 1, y: 0, width: 1, height: 1 },
          { key: '3', label: '3', x: 2, y: 0, width: 1, height: 1 },
          { key: '4', label: '4', x: 3, y: 0, width: 1, height: 1 },
          { key: '5', label: '5', x: 4, y: 0, width: 1, height: 1 },
          { key: '6', label: '6', x: 5, y: 0, width: 1, height: 1 },
          { key: '7', label: '7', x: 6, y: 0, width: 1, height: 1 },
          { key: '8', label: '8', x: 7, y: 0, width: 1, height: 1 },
          { key: '9', label: '9', x: 8, y: 0, width: 1, height: 1 },
          { key: '0', label: '0', x: 9, y: 0, width: 1, height: 1 }
        ],
        [
          { key: 'q', label: 'Q', x: 0, y: 1, width: 1, height: 1 },
          { key: 'w', label: 'W', x: 1, y: 1, width: 1, height: 1 },
          { key: 'e', label: 'E', x: 2, y: 1, width: 1, height: 1 },
          { key: 'r', label: 'R', x: 3, y: 1, width: 1, height: 1 },
          { key: 't', label: 'T', x: 4, y: 1, width: 1, height: 1 },
          { key: 'y', label: 'Y', x: 5, y: 1, width: 1, height: 1 },
          { key: 'u', label: 'U', x: 6, y: 1, width: 1, height: 1 },
          { key: 'i', label: 'I', x: 7, y: 1, width: 1, height: 1 },
          { key: 'o', label: 'O', x: 8, y: 1, width: 1, height: 1 },
          { key: 'p', label: 'P', x: 9, y: 1, width: 1, height: 1 }
        ],
        [
          { key: 'a', label: 'A', x: 0.5, y: 2, width: 1, height: 1 },
          { key: 's', label: 'S', x: 1.5, y: 2, width: 1, height: 1 },
          { key: 'd', label: 'D', x: 2.5, y: 2, width: 1, height: 1 },
          { key: 'f', label: 'F', x: 3.5, y: 2, width: 1, height: 1 },
          { key: 'g', label: 'G', x: 4.5, y: 2, width: 1, height: 1 },
          { key: 'h', label: 'H', x: 5.5, y: 2, width: 1, height: 1 },
          { key: 'j', label: 'J', x: 6.5, y: 2, width: 1, height: 1 },
          { key: 'k', label: 'K', x: 7.5, y: 2, width: 1, height: 1 },
          { key: 'l', label: 'L', x: 8.5, y: 2, width: 1, height: 1 }
        ],
        [
          { key: 'shift', label: '⇧', x: 0, y: 3, width: 1.5, height: 1 },
          { key: 'z', label: 'Z', x: 1.5, y: 3, width: 1, height: 1 },
          { key: 'x', label: 'X', x: 2.5, y: 3, width: 1, height: 1 },
          { key: 'c', label: 'C', x: 3.5, y: 3, width: 1, height: 1 },
          { key: 'v', label: 'V', x: 4.5, y: 3, width: 1, height: 1 },
          { key: 'b', label: 'B', x: 5.5, y: 3, width: 1, height: 1 },
          { key: 'n', label: 'N', x: 6.5, y: 3, width: 1, height: 1 },
          { key: 'm', label: 'M', x: 7.5, y: 3, width: 1, height: 1 },
          { key: 'backspace', label: '⌫', x: 8.5, y: 3, width: 1.5, height: 1 }
        ],
        [
          { key: 'symbol', label: '符号', x: 0, y: 4, width: 1.5, height: 1 },
          { key: 'emoji', label: '😀', x: 1.5, y: 4, width: 1, height: 1 },
          { key: 'comma', label: ',', x: 2.5, y: 4, width: 1, height: 1 },
          { key: 'space', label: '空格', x: 3.5, y: 4, width: 3, height: 1 },
          { key: 'lang-switch', label: '中/En', x: 6.5, y: 4, width: 1.5, height: 1 },
          { key: 'enter', label: '确定', x: 8, y: 4, width: 2, height: 1 }
        ]
      ],
      jiugonggeLayout: [
        // 第一行：数字行
        [
          { key: '1', label: '1', x: 0, y: 0, width: 1, height: 1 },
          { key: '2', label: '2', x: 1, y: 0, width: 1, height: 1 },
          { key: '3', label: '3', x: 2, y: 0, width: 1, height: 1 },
          { key: '4', label: '4', x: 3, y: 0, width: 1, height: 1 },
          { key: '5', label: '5', x: 4, y: 0, width: 1, height: 1 },
          { key: '6', label: '6', x: 5, y: 0, width: 1, height: 1 },
          { key: '7', label: '7', x: 6, y: 0, width: 1, height: 1 },
          { key: '8', label: '8', x: 7, y: 0, width: 1, height: 1 },
          { key: '9', label: '9', x: 8, y: 0, width: 1, height: 1 },
          { key: '0', label: '0', x: 9, y: 0, width: 1, height: 1 }
        ],
        // 第二行：符号 + 九宫格首行 + 功能键
        [
          { key: 'comma', label: ',', x: 0, y: 1, width: 1, height: 1 },
          { key: 'word-division', label: '分词', x: 1, y: 1, width: 1, height: 1 },
          { key: 'abc', label: 'ABC\n2', x: 2, y: 1, width: 1, height: 1, chars: 'ABC' },
          { key: 'def', label: 'DEF\n3', x: 3, y: 1, width: 1, height: 1, chars: 'DEF' },
          { key: 'backspace', label: '×', x: 4, y: 1, width: 1, height: 1 }
        ],
        // 第三行：符号 + 九宫格中行 + 功能键
        [
          { key: 'period', label: '。', x: 0, y: 2, width: 1, height: 1 },
          { key: 'ghi', label: 'GHI\n4', x: 1, y: 2, width: 1, height: 1, chars: 'GHI' },
          { key: 'jkl', label: 'JKL\n5', x: 2, y: 2, width: 1, height: 1, chars: 'JKL' },
          { key: 'mno', label: 'MNO\n6', x: 3, y: 2, width: 1, height: 1, chars: 'MNO' },
          { key: 'delete', label: '🗑', x: 4, y: 2, width: 1, height: 1 }
        ],
        // 第四行：符号 + 九宫格底行 + 功能键
        [
          { key: 'question', label: '?', x: 0, y: 3, width: 1, height: 1 },
          { key: 'pqrs', label: 'PQRS\n7', x: 1, y: 3, width: 1, height: 1, chars: 'PQRS' },
          { key: 'tuv', label: 'TUV\n8', x: 2, y: 3, width: 1, height: 1, chars: 'TUV' },
          { key: 'wxyz', label: 'WXYZ\n9', x: 3, y: 3, width: 1, height: 1, chars: 'WXYZ' },
          { key: 'at', label: '@', x: 4, y: 3, width: 1, height: 1 }
        ],
        // 第五行：底部功能行
        [
          { key: 'symbol', label: '符号', x: 0, y: 4, width: 1, height: 1 },
          { key: 'emoji', label: '😀', x: 1, y: 4, width: 1, height: 1 },
          { key: 'space', label: '————', x: 2, y: 4, width: 1, height: 1 },
          { key: 'lang-switch', label: '中/En', x: 3, y: 4, width: 1, height: 1 },
          { key: 'enter', label: '完成', x: 4, y: 4, width: 1, height: 1 }
        ]
      ]
    }
  },
  computed: {
    currentKeyboardLayout() {
      if (this.keyboardTool.keyboardType === 'qwerty') {
        // 如果是QWERTY键盘，需要根据大小写模式调整字母显示
        return this.qwertyLayout.map(row => 
          row.map(key => {
            // 如果是字母按键，根据大小写模式调整显示
            if (/^[a-z]$/.test(key.key)) {
              return {
                ...key,
                label: this.keyboardTool.isUpperCase ? key.label.toUpperCase() : key.key.toLowerCase()
              };
            }
            return key;
          })
        );
      } else {
        return this.jiugonggeLayout;
      }
    },
    hasAnyKeyBackground() {
      return Object.keys(this.keyboardTool.keyBackgrounds).length > 0
    }
  },
  methods: {
    handleKeyBgSelect(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.keyboardTool.draggedImage = e.target.result;
          this.showToast('图片已选择，请点击要设置背景的按键', 'info');
        };
        reader.readAsDataURL(file);
      }
    },
    
    handleKeyDrop(event, key) {
      event.preventDefault();
      event.stopPropagation();
      
      // 清除拖拽状态
      this.keyboardTool.dragOverKey = null;
      
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.keyboardTool.keyBackgrounds[key.key] = e.target.result;
            // 显示成功提示
            this.showToast(`已为 ${key.label} 键设置背景图片`);
          };
          reader.readAsDataURL(file);
        } else {
          this.showToast('请拖拽图片文件（JPG、PNG、GIF、WebP）', 'error');
        }
      }
    },
    
    handleKeyDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    
    handleKeyDragEnter(event, key) {
      event.preventDefault();
      // 检查是否为图片文件
      const items = event.dataTransfer.items;
      let hasImage = false;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          hasImage = true;
          break;
        }
      }
      
      if (hasImage) {
        this.keyboardTool.dragOverKey = key.key;
      }
    },
    
    handleKeyDragLeave(event) {
      // 检查是否真的离开了按键区域
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;
      
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        this.keyboardTool.dragOverKey = null;
      }
    },
    
    handleKeyClick(key) {
      if (this.keyboardTool.draggedImage) {
        // 如果有拖拽的图片，设置为按键背景
        this.keyboardTool.keyBackgrounds[key.key] = this.keyboardTool.draggedImage;
        this.keyboardTool.draggedImage = null;
        this.showToast(`已为 ${key.label} 键设置背景图片`);
      }
    },
    
    handleKeyDoubleClick(key) {
      // 双击移除按键背景图片
      if (this.keyboardTool.keyBackgrounds[key.key]) {
        delete this.keyboardTool.keyBackgrounds[key.key];
        this.showToast(`已移除 ${key.label} 键的背景图片`);
      }
    },
    
    clearAllKeyBackgrounds() {
      if (confirm('确定要清除所有按键背景图片吗？')) {
        this.keyboardTool.keyBackgrounds = {};
        this.showToast('已清除所有按键背景图片');
      }
    },
    
    getKeyStyle(key) {
      if (this.keyboardTool.keyboardType === 'jiugongge') {
        return {
          left: (key.x * 75 + 5) + 'px',
          top: (key.y * 54) + 'px',
          width: (key.width * 75 - 6) + 'px',
          height: (key.height * 54 - 6) + 'px'
        };
      } else {
        return {
          left: (key.x * 40 + 2) + 'px',
          top: (key.y * 44) + 'px',
          width: (key.width * 40 - 6) + 'px',
          height: (key.height * 44 - 6) + 'px'
        };
      }
    },
    

    
    async exportRenamedImages() {
      const keyBackgrounds = this.keyboardTool.keyBackgrounds;
      const skinNumber = this.keyboardTool.skinNumber;
      const keyboardType = this.keyboardTool.keyboardType;
      
      if (Object.keys(keyBackgrounds).length === 0) {
        this.showToast('没有找到按键背景图片', 'error');
        return;
      }
      
      this.showToast('正在生成压缩包...', 'info');
      
      try {
        // 创建ZIP实例
        const zip = new JSZip();
        let exportCount = 0;
        
        // 遍历所有按键背景图片
        for (const [keyCode, imageDataUrl] of Object.entries(keyBackgrounds)) {
          try {
            // 获取按键的显示名称
            const keyInfo = this.getKeyInfo(keyCode);
            if (!keyInfo) continue;
            
            // 生成文件名
            const fileName = this.generateFileName(skinNumber, keyboardType, keyCode);
            
            // 将 base64 转换为 blob
            const response = await fetch(imageDataUrl);
            const blob = await response.blob();
            
            // 添加文件到ZIP
            zip.file(fileName, blob);
            exportCount++;
            
          } catch (error) {
            console.error(`处理 ${keyCode} 按键图片失败:`, error);
          }
        }
        
        if (exportCount === 0) {
          this.showToast('没有有效的图片可以导出', 'error');
          return;
        }
        
        // 生成ZIP文件
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        
        // 创建下载链接
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `skin${skinNumber}_${keyboardType}_keyboard_backgrounds.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        this.showToast(`成功导出包含 ${exportCount} 张图片的压缩包`);
        
      } catch (error) {
        console.error('导出压缩包失败:', error);
        this.showToast('导出压缩包失败，请重试', 'error');
      }
    },
    
    getKeyInfo(keyCode) {
      // 查找按键信息
      for (const row of this.currentKeyboardLayout) {
        for (const key of row) {
          if (key.key === keyCode) {
            return key;
          }
        }
      }
      return null;
    },
    
    generateFileName(skinNumber, keyboardType, keyCode) {
      // 根据是否为桌宠皮肤决定前缀
      const skinPrefix = this.keyboardTool.isPetSkin ? `pet${skinNumber}` : `skin${skinNumber}`;
      
      // 数字键的命名格式根据键盘类型区分
      if (/^[0-9]$/.test(keyCode)) {
        if (keyboardType === 'jiugongge') {
          // 九宫格数字键：pet1_bg_t9_number_4_background
          return `${skinPrefix}_bg_t9_number_${keyCode}_background`;
        } else {
          // QWERTY数字键：pet1_bg_t9_number_4（简化格式）
          return `${skinPrefix}_bg_t9_number_${keyCode}`;
        }
      }
      
      // 如果是九宫格键盘，使用特殊的T9命名格式
      if (keyboardType === 'jiugongge') {
        // 九宫格T9按键映射 (按键1-9对应分词按键到WXYZ按键)
        const t9KeyMap = {
          'word-division': 'key_1',  // 分词按键
          'abc': 'key_2',            // ABC按键
          'def': 'key_3',            // DEF按键  
          'ghi': 'key_4',            // GHI按键
          'jkl': 'key_5',            // JKL按键
          'mno': 'key_6',            // MNO按键
          'pqrs': 'key_7',           // PQRS按键
          'tuv': 'key_8',            // TUV按键
          'wxyz': 'key_9'            // WXYZ按键
        };
        
        // 如果是T9核心按键，使用新格式
        if (t9KeyMap[keyCode]) {
          return `${skinPrefix}_t9_${t9KeyMap[keyCode]}_background.png`;
        }
        
        // 空格键使用特殊的T9格式
        if (keyCode === 'space') {
          return `${skinPrefix}_t9_space_background.png`;
        }
        
        // 符号键和😀键使用特殊的T9格式
        if (keyCode === 'symbol') {
          return `${skinPrefix}_t9_symbol_background.png`;
        }
        if (keyCode === 'emoji') {
          return `${skinPrefix}_t9_emoji_background.png`;
        }
        
        // 回车键使用特殊的T9格式
        if (keyCode === 'enter') {
          return `${skinPrefix}_t9_enter_background.png`;
        }
        
        // 🗑键和×键使用特殊的T9格式
        if (keyCode === 'delete') {
          return `${skinPrefix}_t9_repeat_background.png`;
        }
        if (keyCode === 'backspace') {
          return `${skinPrefix}_t9_delete_background.png`;
        }
        
        // @键使用特殊的T9格式
        if (keyCode === 'at') {
          return `${skinPrefix}_t9_at_background.png`;
        }
        
        // 其他九宫格按键使用原格式
        const jiugonggeKeyNameMap = {
          // 九宫格功能按键
          'comma': 'comma',
          'period': 'period', 
          'question': 'question',
          'lang-switch': 'lang_switch'
        };
        
        const keyName = jiugonggeKeyNameMap[keyCode] || keyCode;
        return `${skinPrefix}_jiugongge_${keyName}_background.png`;
      }
      
      // QWERTY键盘使用原有的命名格式
      // 符号键使用简化格式
      if (keyCode === 'symbol') {
        return `${skinPrefix}_symbol_background.png`;
      }
      if (keyCode === 'emoji') {
        return `${skinPrefix}_qwerty_emoji_background.png`;
      }
      
      // 回车键使用特殊格式
      if (keyCode === 'enter') {
        return `${skinPrefix}_qwerty_enter_background.png`;
      }
      
      // ⌫键使用特殊格式
      if (keyCode === 'backspace') {
        return `${skinPrefix}_qwerty_delete_background.png`;
      }
      
      // 判断是否为字母按键
      const isLetter = /^[a-z]$/.test(keyCode);
      
      if (isLetter) {
        // 字母按键根据大小写模式生成不同的文件名
        if (this.keyboardTool.isUpperCase) {
          // 大写模式：pet1_qwerty_a_upper_background.png 或 skin1_qwerty_a_upper_background.png
          return `${skinPrefix}_qwerty_${keyCode}_upper_background.png`;
        } else {
          // 小写模式：pet1_qwerty_a_background.png 或 skin1_qwerty_a_background.png
          return `${skinPrefix}_qwerty_${keyCode}_background.png`;
        }
      }
      
      // 特殊功能按键使用简化格式
      if (keyCode === 'comma') {
        return `${skinPrefix}_comma_background.png`;
      }
      if (keyCode === 'lang-switch') {
        return `${skinPrefix}_lang_switch_background.png`;
      }
      
      // 其他非字母按键的功能按键
      const keyNameMap = {
        // 功能按键
        'shift': 'shift',
        'space': 'space'
      };
      
      const keyName = keyNameMap[keyCode] || keyCode;
      return `${skinPrefix}_qwerty_${keyName}_background.png`;
    },
    
    showToast(message, type = 'success') {
      // 创建toast元素
      const toast = document.createElement('div');
      toast.className = `toast toast-${type}`;
      toast.textContent = message;
      
      // 添加到页面
      document.body.appendChild(toast);
      
      // 显示动画
      setTimeout(() => {
        toast.classList.add('show');
      }, 100);
      
      // 3秒后移除
      setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      }, 3000);
    }
  }
}
</script>

<style scoped>
.keyboard-tool-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Poppins', 'Helvetica Neue', Arial, sans-serif;
}

.tool-header {
  text-align: center;
  margin-bottom: 30px;
  color: white;
}

.tool-header h1 {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 10px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.tool-header p {
  font-size: 16px;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
}

.tool-content {
  max-width: 1200px;
  margin: 0 auto;
}

.tool-controls {
  background: white;
  padding: 25px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.control-group label {
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.keyboard-type-switch {
  display: flex;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
}

.case-switch {
  display: flex;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
}

.type-btn {
  padding: 10px 20px;
  border: none;
  background-color: white;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border-right: 1px solid #d1d5db;
}

.case-btn {
  padding: 10px 16px;
  border: none;
  background-color: white;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border-right: 1px solid #d1d5db;
  font-size: 14px;
}

.type-btn:last-child,
.case-btn:last-child {
  border-right: none;
}

.type-btn:hover,
.case-btn:hover {
  background-color: #e5e7eb;
}

.type-btn.active,
.case-btn.active {
  background-color: #3b82f6;
  color: white;
}

.control-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #3b82f6;
  color: white;
}

.control-btn:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
}

.control-btn.secondary {
  background-color: #6b7280;
}

.control-btn.secondary:hover {
  background-color: #4b5563;
}

.control-btn.primary {
  background-color: #10b981;
}

.control-btn.primary:hover {
  background-color: #059669;
}

.control-btn.active {
  background-color: #ec4899;
}

.control-btn.active:hover {
  background-color: #db2777;
}

.control-btn.success {
  background-color: #059669;
}

.control-btn.success:hover {
  background-color: #047857;
}

.skin-number-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  width: 80px;
  text-align: center;
  transition: border-color 0.2s;
}

.skin-number-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.show-labels-checkbox {
  margin-right: 8px;
  transform: scale(1.2);
  accent-color: #3b82f6;
}

.pet-skin-checkbox {
  margin-right: 8px;
  transform: scale(1.2);
  accent-color: #3b82f6;
}

.keyboard-preview-container {
  display: flex;
  justify-content: center;
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.keyboard-container {
  background: #f3f4f6;
  position: relative;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.drag-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.05);
  border: 2px dashed #9ca3af;
  border-radius: 8px;
}

.drag-hint {
  text-align: center;
}

.drag-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.drag-hint p {
  color: #6b7280;
  font-size: 16px;
  margin: 8px 0;
  font-weight: 600;
}

.drag-hint .sub-hint {
  font-size: 12px;
  color: #9ca3af;
  font-weight: normal;
}

.keyboard-layout {
  position: relative;
  width: 420px;
  height: 230px;
  margin: 0 auto;
  transition: all 0.3s;
}

.keyboard-layout.jiugongge {
  width: 380px;
  height: 270px;
}

.keyboard-row {
  position: relative;
  height: 0px;
  margin-bottom: 2px;
}

.keyboard-key {
  position: absolute;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #d1d5db;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(5px);
  overflow: hidden; 
}

.keyboard-key:hover {
  border-color: #3b82f6;
  transform: translateY(-1px);
}



.keyboard-key.has-background {
  border-color: #f59e0b;
  border-width: 2px;
  background: transparent;
}

.keyboard-key.jiugongge-key {
  border-radius: 8px;
}

.keyboard-key.drag-over {
  border-color: #10b981 !important;
  border-width: 3px !important;
  background: rgba(16, 185, 129, 0.1) !important;
  transform: scale(1.05);
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.3);
}

.key-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 1;
  border-radius: inherit;
}

.key-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  min-width: 80%;
  min-height: 80%;
  transition: all 0.3s ease;
}

/* 当按键有背景图片时，默认隐藏文字内容 */
.keyboard-key.has-background .key-content {
  background: transparent;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

/* 当选择显示文字时，让文字可见 */
.keyboard-key.has-background.show-labels .key-content {
  opacity: 1;
  pointer-events: auto;
}

.keyboard-key.has-background.show-labels .key-label {
  color: white;
  text-shadow: 
    1px 1px 2px rgba(0, 0, 0, 0.8),
    -1px -1px 2px rgba(0, 0, 0, 0.8),
    1px -1px 2px rgba(0, 0, 0, 0.8),
    -1px 1px 2px rgba(0, 0, 0, 0.8);
  font-weight: 700;
}

/* 为有背景但隐藏文字的按键添加一个小图标指示器 */
.keyboard-key.has-background:not(.show-labels)::after {
  content: '';
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  z-index: 3;
}

.key-label {
  font-size: 10px;
  font-weight: 600;
  color: #374151;
  line-height: 1.2;
  white-space: pre-line;
}

.jiugongge-key .key-label {
  font-size: 12px;
}

@media (max-width: 768px) {
  .tool-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
  
  .control-group {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .keyboard-type-switch {
    width: 100%;
  }
  
  .type-btn {
    flex: 1;
  }
  
  .keyboard-container {
    padding: 15px;
  }
  
  .keyboard-layout {
    width: 320px;
    height: 200px;
    transform: scale(0.8);
    transform-origin: center;
  }
  
  .keyboard-layout.jiugongge {
    width: 300px;
    height: 200px;
  }
}

/* Toast 提示样式 */
:global(.toast) {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  z-index: 10000;
  transform: translateX(100%);
  opacity: 0;
  transition: all 0.3s ease;
  max-width: 300px;
  word-wrap: break-word;
}

:global(.toast.show) {
  transform: translateX(0);
  opacity: 1;
}

:global(.toast.toast-success) {
  background-color: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

:global(.toast.toast-error) {
  background-color: #ef4444;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
}

:global(.toast.toast-info) {
  background-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}
</style> 